import type { Flow } from "@cogo/types-v2"
import type { ActionConsumerContract, RuntimeActionHandler } from "./action-consumer-contract"
import { SubscriptionRegistry, type RealtimeChannelLike } from "./subscription-registry"

// ---------------------------------------------------------------------------
// Types — generic interface so cogo-runtime has no @supabase/supabase-js dep
// ---------------------------------------------------------------------------

/** Minimal subset of SupabaseClient used by registered actions. */
export interface SupabaseLikeClient {
  auth: {
    // Existing methods
    signInWithPassword(creds: { email: string; password: string }): Promise<{ data: any; error: any }>
    signOut(): Promise<{ error: any }>
    getSession(): Promise<{ data: { session: any } }>
    getUser(): Promise<{ data: { user: any } }>
    // AUTH-01: signUp
    signUp(creds: { email: string; password: string; options?: { data?: Record<string, unknown> } }): Promise<{ data: any; error: any }>
    // AUTH-02: resetPasswordForEmail
    resetPasswordForEmail(email: string, options?: { redirectTo?: string }): Promise<{ data: any; error: any }>
    // AUTH-03: updateUser
    updateUser(attrs: { email?: string; password?: string; data?: Record<string, unknown> }): Promise<{ data: any; error: any }>
    // AUTH-04: onAuthStateChange
    onAuthStateChange(callback: (event: string, session: any) => void): { data: { subscription: { unsubscribe(): void } } }
    // AUTH-05: signInWithOAuth
    signInWithOAuth(opts: { provider: string; options?: { redirectTo?: string; scopes?: string; queryParams?: Record<string, string> } }): Promise<{ data: any; error: any }>
    // AUTH-07: MFA
    mfa: {
      enroll(params: { factorType: 'totp'; friendlyName?: string }): Promise<{ data: any; error: any }>
      challenge(params: { factorId: string }): Promise<{ data: any; error: any }>
      verify(params: { factorId: string; challengeId: string; code: string }): Promise<{ data: any; error: any }>
      unenroll(params: { factorId: string }): Promise<{ data: any; error: any }>
      listFactors(): Promise<{ data: any; error: any }>
    }
  }
  storage: {
    from(bucket: string): {
      // Existing methods
      list(path?: string): Promise<{ data: any; error: any }>
      download(path: string): Promise<{ data: any; error: any }>
      remove(paths: string[]): Promise<{ error: any }>
      getPublicUrl(path: string): { data: { publicUrl: string } }
      // STOR-01: upload
      upload(path: string, file: File | Blob, options?: { upsert?: boolean; contentType?: string }): Promise<{ data: any; error: any }>
      // STOR-02: createSignedUrl
      createSignedUrl(path: string, expiresIn: number): Promise<{ data: { signedUrl: string } | null; error: any }>
    }
  }
  // RT: Realtime channel management
  channel(name: string, opts?: {
    config?: {
      broadcast?: { self?: boolean; ack?: boolean }
      presence?: { key?: string }
      private?: boolean
    }
  }): any
  removeChannel(channel: any): Promise<'ok' | 'timed out' | 'error'>
  getChannels(): any[]
  removeAllChannels(): Promise<('ok' | 'timed out' | 'error')[]>
}

/** State accessor interface for the action engine. */
export interface ActionEngineState {
  setState(path: string, value: unknown): void
  resolve(template: string): unknown
}

/** Minimal action engine interface accepted by registerSupabaseActions. */
export interface SupabaseActionEngine {
  registerBuiltin(contract: ActionConsumerContract, handler: RuntimeActionHandler): void
  state: ActionEngineState
  compute: { evaluate(expr: string): unknown }
  /** Optional: trigger flow execution for event-driven actions (D-08) */
  executeFlow?(flow: Flow, context?: Record<string, unknown>): Promise<void>
  /** Optional: look up a named flow from the registry (for triggerFlow support) */
  findFlow?(flowName: string): Flow | undefined
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Register secure Supabase actions that use the JS client (not raw REST).
 * These OVERRIDE the built-in supabaseQuery/supabaseRpc from cogo-runtime,
 * replacing the insecure apiKey-in-params pattern.
 *
 * Actions registered:
 *   supabaseQuery    - RLS-protected CRUD (select/insert/update/delete)
 *   supabaseRpc      - RPC calls with auth error detection + retry
 *   supabaseAuth     - Auth operations (signIn/signOut/getSession/getUser)
 *   supabaseStorage  - Storage operations (list/download/remove/getPublicUrl)
 *
 * @param engine   - ActionFlowEngine or compatible action engine
 * @param supabase - SupabaseClient instance (from @supabase/supabase-js)
 */
export function registerSupabaseActions(
  engine: SupabaseActionEngine,
  supabase: SupabaseLikeClient,
): void {
  // --- supabaseQuery: Routes through Next.js API proxy for RLS bypass ---
  engine.registerBuiltin(supabaseContract('supabaseQuery', [
    'table', 'method', 'resultTarget', 'columns', 'filters', 'order', 'range', 'single', 'count', 'data',
  ], 'resultTarget', undefined, { prefix: '_results.', param: 'table' }, ['table']), async (params) => {
    const table = String(params.table ?? '').trim()
    if (!table) throw new Error('supabaseQuery requires a non-blank table')
    const method = String(params.method ?? 'select')
    const resultTarget = String(params.resultTarget ?? `_results.${table}`).replace(/^#/, '')
    const columns = params.columns as string | undefined
    const filters = (params.filters ?? []) as Array<{ column: string; op: string; value: unknown; skipIf?: string }>
    const rawOrder = params.order as
      | { column: string; ascending?: boolean }
      | Array<{ column: string; ascending?: boolean }>
      | undefined
    const range = params.range as { from: number; to: number } | undefined
    const single = params.single === true
    const countMode = params.count as string | undefined

    // Normalize order: accept both single object and array
    const orderList: Array<{ column: string; ascending?: boolean }> = rawOrder
      ? (Array.isArray(rawOrder) ? rawOrder : [rawOrder])
      : []

    try {
      if (method === 'select') {
        // Build PostgREST query string
        const qp = new URLSearchParams()
        qp.set('select', columns ?? '*')
        for (const f of filters) {
          if (f.skipIf) {
            const skipVal = engine.state.resolve(String(f.skipIf))
            if (skipVal) continue
          }
          qp.append(f.column, `${f.op}.${f.value}`)
        }
        for (const o of orderList) {
          qp.append('order', `${o.column}.${o.ascending === false ? 'desc' : 'asc'}`)
        }
        if (single) qp.set('limit', '1')

        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (range) {
          headers['Range'] = `${Number(range.from) || 0}-${Number(range.to) || 19}`
          headers['Range-Unit'] = 'items'
        }
        if (countMode === 'exact') {
          headers['Prefer'] = 'count=exact'
        }

        const res = await fetch(`/api/pg/rest/${table}?${qp.toString()}`, {
          method: 'GET',
          headers,
          credentials: 'include',
        })
        const json: any = await res.json()
        if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : JSON.stringify(json.error))

        // Parse count from Content-Range header or response
        let count: number | null = null
        if (countMode === 'exact') {
          const cr = res.headers.get('content-range')
          if (cr) {
            const m = cr.match(/\/(\d+)/)
            if (m) count = parseInt(m[1], 10)
          }
          // Fallback: if data is array, use length (for small result sets)
          if (count === null && Array.isArray(json.data)) {
            count = json.data.length
          }
        }

        const data = single && Array.isArray(json.data) ? json.data[0] ?? null : json.data
        engine.state.setState(resultTarget, { data, count })
      } else if (method === 'insert') {
        const res = await fetch(`/api/pg/rest/${table}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
          credentials: 'include',
          body: JSON.stringify(params.data),
        })
        const json: any = await res.json()
        if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : JSON.stringify(json.error))
        engine.state.setState(resultTarget, { data: json.data })
      } else if (method === 'update') {
        const qp = new URLSearchParams()
        for (const f of filters) {
          if (f.skipIf) {
            const skipVal = engine.state.resolve(String(f.skipIf))
            if (skipVal) continue
          }
          qp.append(f.column, `${f.op}.${f.value}`)
        }
        const res = await fetch(`/api/pg/rest/${table}?${qp.toString()}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
          credentials: 'include',
          body: JSON.stringify(params.data),
        })
        const json: any = await res.json()
        if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : JSON.stringify(json.error))
        engine.state.setState(resultTarget, { data: json.data })
      } else if (method === 'delete') {
        const qp = new URLSearchParams()
        for (const f of filters) {
          if (f.skipIf) {
            const skipVal = engine.state.resolve(String(f.skipIf))
            if (skipVal) continue
          }
          qp.append(f.column, `${f.op}.${f.value}`)
        }
        const res = await fetch(`/api/pg/rest/${table}?${qp.toString()}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        })
        if (!res.ok) {
          const json: any = await res.json().catch(() => ({ error: 'Delete failed' }))
          throw new Error(typeof json.error === 'string' ? json.error : JSON.stringify(json.error))
        }
        engine.state.setState(resultTarget, { success: true })
      }
    } catch (err: any) {
      engine.state.setState(resultTarget, { error: err.message ?? String(err) })
    }
  })

  // --- supabaseAuth: sign in/out/session ---
  engine.registerBuiltin(supabaseContract('supabaseAuth', [
    'method', 'resultTarget', 'email', 'password', 'metadata', 'redirectTo', 'triggerFlow',
    'provider', 'scopes', 'friendlyName', 'factorId', 'code',
  ], 'resultTarget', '_results.auth'), async (params) => {
    const method = String(params.method ?? '')
    const resultTarget = String(params.resultTarget ?? '_results.auth').replace(/^#/, '')

    try {
      if (method === 'signInWithPassword') {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: String(params.email ?? ''),
          password: String(params.password ?? ''),
        })
        if (error) throw error
        engine.state.setState(resultTarget, { session: data.session, user: data.user })
      } else if (method === 'signOut') {
        const { error } = await supabase.auth.signOut()
        if (error) throw error
        engine.state.setState(resultTarget, { success: true })
      } else if (method === 'getSession') {
        const { data } = await supabase.auth.getSession()
        engine.state.setState(resultTarget, { session: data.session })
      } else if (method === 'getUser') {
        const { data } = await supabase.auth.getUser()
        engine.state.setState(resultTarget, { user: data.user })

      // AUTH-01: signUp
      } else if (method === 'signUp') {
        const { data, error } = await supabase.auth.signUp({
          email: String(params.email ?? ''),
          password: String(params.password ?? ''),
          options: params.metadata ? { data: params.metadata as Record<string, unknown> } : undefined,
        })
        if (error) throw error
        engine.state.setState(resultTarget, { session: data.session, user: data.user })

      // AUTH-02: resetPasswordForEmail
      } else if (method === 'resetPasswordForEmail') {
        const opts = params.redirectTo ? { redirectTo: String(params.redirectTo) } : undefined
        const { error } = await supabase.auth.resetPasswordForEmail(String(params.email ?? ''), opts)
        if (error) throw error
        engine.state.setState(resultTarget, { success: true })

      // AUTH-03: updateUser
      } else if (method === 'updateUser') {
        const attrs: Record<string, unknown> = {}
        if (params.email) attrs.email = String(params.email)
        if (params.password) attrs.password = String(params.password)
        if (params.metadata) attrs.data = params.metadata as Record<string, unknown>
        const { data, error } = await supabase.auth.updateUser(attrs as any)
        if (error) throw error
        engine.state.setState(resultTarget, { user: data.user })

      // AUTH-04: onAuthStateChange (D-08: trigger ActionFlow)
      } else if (method === 'onAuthStateChange') {
        const triggerFlowName = String(params.triggerFlow ?? 'authStateChange')
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          // Per D-09: Restore flow context from sessionStorage after OAuth redirect
          if (event === 'SIGNED_IN' && typeof sessionStorage !== 'undefined') {
            const savedContext = sessionStorage.getItem('cogo_oauth_flow_context')
            if (savedContext) {
              try {
                const flowContext = JSON.parse(savedContext)
                engine.state.setState('_flowContext', flowContext)
                sessionStorage.removeItem('cogo_oauth_flow_context')
              } catch { /* ignore parse errors */ }
            }
          }

          // 1. Store event data in state for expression access
          engine.state.setState(resultTarget, { event, session })

          // 2. Per D-08: Trigger authStateChange ActionFlow if engine supports executeFlow
          if (engine.executeFlow) {
            const flow = engine.findFlow?.(triggerFlowName)
            if (flow) {
              engine.executeFlow(flow, { event, session }).catch((err) => {
                console.warn('[supabaseAuth] authStateChange flow trigger failed:', err)
              })
            } else {
              console.warn(`[supabaseAuth] Flow "${triggerFlowName}" not found in registry`)
            }
          }
        })
        // Store unsubscribe fn so it can be cleaned up later (Phase 74 SubscriptionRegistry)
        engine.state.setState('_subscriptions.authStateChange', { unsubscribe: () => subscription.unsubscribe() })

      // AUTH-05 + AUTH-06: signInWithOAuth (D-09 save, D-11, D-12, D-13)
      } else if (method === 'signInWithOAuth') {
        const provider = String(params.provider ?? 'google')
        const options: Record<string, unknown> = {}
        if (params.redirectTo) options.redirectTo = String(params.redirectTo)
        if (params.scopes) options.scopes = String(params.scopes)
        options.queryParams = { access_type: 'offline' }

        // Per D-09: Save flow context to sessionStorage before potential redirect
        if (typeof sessionStorage !== 'undefined') {
          const flowContext = engine.state.resolve('#_flowContext')
          if (flowContext) {
            sessionStorage.setItem('cogo_oauth_flow_context', JSON.stringify(flowContext))
          }
        }

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options,
        })
        if (error) throw error

        // Per D-12: popup mode default, redirect fallback
        if (data?.url) {
          if (typeof window !== 'undefined') {
            const popup = window.open(data.url, '_blank', 'width=500,height=600,popup=yes')
            if (!popup || popup.closed) {
              // Popup blocked — fallback to redirect
              window.location.href = data.url
            }
          }
        }
        engine.state.setState(resultTarget, { data })

      // AUTH-07: MFA — enrollMfa (D-14 TOTP)
      } else if (method === 'enrollMfa') {
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: params.friendlyName ? String(params.friendlyName) : undefined,
        })
        if (error) throw error
        engine.state.setState(resultTarget, { factor: data })

      // AUTH-07: MFA — verifyMfa (challenge + verify two-step)
      } else if (method === 'verifyMfa') {
        const factorId = String(params.factorId ?? '')
        const code = String(params.code ?? '')
        // Step 1: Create challenge
        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })
        if (challengeError) throw challengeError
        // Step 2: Verify with code
        const { data, error } = await supabase.auth.mfa.verify({
          factorId,
          challengeId: challengeData.id,
          code,
        })
        if (error) throw error
        engine.state.setState(resultTarget, { session: data })

      // AUTH-07: MFA — unenrollMfa
      } else if (method === 'unenrollMfa') {
        const { error } = await supabase.auth.mfa.unenroll({
          factorId: String(params.factorId ?? ''),
        })
        if (error) throw error
        engine.state.setState(resultTarget, { success: true })

      // AUTH-07: MFA — listMfaFactors
      } else if (method === 'listMfaFactors') {
        const { data, error } = await supabase.auth.mfa.listFactors()
        if (error) throw error
        engine.state.setState(resultTarget, { factors: data })
      }
    } catch (err: any) {
      engine.state.setState(resultTarget, { error: err.message ?? String(err) })
    }
  })

  // --- supabaseRpc: Routes through Next.js API proxy for RLS bypass ---
  engine.registerBuiltin(supabaseContract('supabaseRpc', [
    'functionName', 'args', 'resultTarget', 'retryCount',
  ], 'resultTarget', '_results.rpc'), async (params) => {
    const functionName = String(params.functionName ?? '')
    const args = (params.args ?? {}) as Record<string, unknown>
    const resultTarget = String(params.resultTarget ?? '_results.rpc').replace(/^#/, '')
    const maxRetries = Math.min(Number(params.retryCount ?? 0), 2)

    let lastError: any = null
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const res = await fetch(`/api/pg/rpc/${functionName}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(args),
        })
        const json: any = await res.json()
        if (!res.ok) throw new Error(typeof json.error === 'string' ? json.error : JSON.stringify(json.error))
        engine.state.setState(resultTarget, { data: json.data })
        return
      } catch (err: any) {
        lastError = err
        const msg = (err.message ?? String(err)).toLowerCase()
        // Auth errors — no point retrying
        if (msg.includes('jwt') || msg.includes('token') || msg.includes('unauthorized')) {
          engine.state.setState('auth.isAuthenticated', false)
          break
        }
        // Only retry on potentially transient errors
        if (attempt < maxRetries) continue
      }
    }
    engine.state.setState(resultTarget, { error: lastError?.message ?? String(lastError) })
  })

  // --- supabaseStorage: file upload/download ---
  engine.registerBuiltin(supabaseContract('supabaseStorage', [
    'bucket', 'method', 'path', 'resultTarget', 'paths', 'file', 'upsert', 'contentType', 'expiresIn',
  ], 'resultTarget', '_results.storage'), async (params) => {
    const bucket = String(params.bucket ?? '')
    const method = String(params.method ?? 'list')
    const path = String(params.path ?? '')
    const resultTarget = String(params.resultTarget ?? '_results.storage').replace(/^#/, '')

    try {
      if (method === 'list') {
        const { data, error } = await supabase.storage.from(bucket).list(path)
        if (error) throw error
        engine.state.setState(resultTarget, { data })
      } else if (method === 'download') {
        const { data, error } = await supabase.storage.from(bucket).download(path)
        if (error) throw error
        const url = URL.createObjectURL(data)
        engine.state.setState(resultTarget, { url })
      } else if (method === 'remove') {
        const paths = (params.paths ?? [path]) as string[]
        const { error } = await supabase.storage.from(bucket).remove(paths)
        if (error) throw error
        engine.state.setState(resultTarget, { success: true })
      } else if (method === 'getPublicUrl') {
        const { data } = supabase.storage.from(bucket).getPublicUrl(path)
        engine.state.setState(resultTarget, { publicUrl: data.publicUrl })
      } else if (method === 'upload') {
        // STOR-01: Upload file to bucket, return publicUrl for immediate use (per D-10)
        const file = params.file as File | Blob | undefined
        if (!file) throw new Error('File is required for upload')
        const uploadOpts: Record<string, unknown> = {}
        if (params.upsert != null) uploadOpts.upsert = Boolean(params.upsert)
        if (params.contentType) uploadOpts.contentType = String(params.contentType)
        const { data, error } = await supabase.storage.from(bucket).upload(path, file, uploadOpts as any)
        if (error) throw error
        // Return publicUrl for immediate consumption in next flow step
        const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path)
        engine.state.setState(resultTarget, { publicUrl: urlData.publicUrl, path: data?.path ?? path })
      } else if (method === 'createSignedUrl') {
        // STOR-02: Create time-limited signed URL for private file access
        const expiresIn = Number(params.expiresIn ?? 3600)
        const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn)
        if (error) throw error
        engine.state.setState(resultTarget, { signedUrl: data?.signedUrl ?? '' })
      }
    } catch (err: any) {
      engine.state.setState(resultTarget, { error: err.message ?? String(err) })
    }
  })

  // --- supabaseRealtime: realtime subscriptions, broadcast, presence ---
  const registry = new SubscriptionRegistry()
  engine.state.setState('_subscriptionRegistry', registry)

  engine.registerBuiltin(supabaseContract('supabaseRealtime', [
    'action', 'channel', 'eventType', 'triggerFlow', 'event', 'schema', 'table', 'filter',
    'resultTarget', 'payload', 'state',
  ], 'resultTarget', '_results.realtime'), async (params) => {
    const action = String(params.action ?? '')
    const channelName = String(params.channel ?? '')

    if (action === 'subscribe') {
      if (registry.get(channelName)) {
        throw new Error(`[supabaseRealtime] Channel "${channelName}" already subscribed. Unsubscribe first.`)
      }
      const eventType = String(params.eventType ?? 'postgres_changes')
      const triggerFlowName = params.triggerFlow ? String(params.triggerFlow) : ''
      const ch = supabase.channel(channelName)

      if (eventType === 'postgres_changes') {
        ch.on('postgres_changes', {
          event: String(params.event ?? '*'),
          schema: String(params.schema ?? 'public'),
          table: params.table ? String(params.table) : undefined,
          filter: params.filter ? String(params.filter) : undefined,
        }, (payload: unknown) => {
          engine.state.setState('_results.realtimePayload', payload)
          if (triggerFlowName && engine.executeFlow) {
            const flow = engine.findFlow?.(triggerFlowName)
            if (flow) {
              engine.executeFlow(flow, { payload }).catch(err => console.warn('[supabaseRealtime] trigger failed:', err))
            } else {
              console.warn(`[supabaseRealtime] Flow "${triggerFlowName}" not found in registry`)
            }
          }
        })
      } else if (eventType === 'broadcast') {
        ch.on('broadcast', { event: String(params.event ?? '*') }, (payload: unknown) => {
          engine.state.setState('_results.realtimePayload', payload)
          if (triggerFlowName && engine.executeFlow) {
            const flow = engine.findFlow?.(triggerFlowName)
            if (flow) {
              engine.executeFlow(flow, { payload }).catch(err => console.warn('[supabaseRealtime] trigger failed:', err))
            } else {
              console.warn(`[supabaseRealtime] Flow "${triggerFlowName}" not found in registry`)
            }
          }
        })
      } else if (eventType === 'presence') {
        ch.on('presence', { event: 'sync' }, () => {
          const state = ch.presenceState()
          const presenceList = Object.values(state).flat()
          engine.state.setState(`presence.${channelName}`, presenceList)
          if (triggerFlowName && engine.executeFlow) {
            const flow = engine.findFlow?.(triggerFlowName)
            if (flow) {
              engine.executeFlow(flow, { presenceState: presenceList }).catch(err => console.warn('[supabaseRealtime] trigger failed:', err))
            } else {
              console.warn(`[supabaseRealtime] Flow "${triggerFlowName}" not found in registry`)
            }
          }
        })
      }

      ch.subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          engine.state.setState(
            String(params.resultTarget ?? '_results.realtime').replace(/^#/, ''),
            { subscribed: true, channel: channelName },
          )
        }
      })
      registry.add(channelName, ch)

    } else if (action === 'broadcast') {
      const ch = registry.get(channelName)
      if (!ch) throw new Error(`[supabaseRealtime] Channel "${channelName}" not found. Subscribe first.`)
      await ch.send({
        type: 'broadcast',
        event: String(params.event ?? 'message'),
        payload: (params.payload as Record<string, unknown>) ?? {},
      })

    } else if (action === 'trackPresence') {
      const ch = registry.get(channelName)
      if (!ch) throw new Error(`[supabaseRealtime] Channel "${channelName}" not found. Subscribe first.`)
      await ch.track((params.state as Record<string, unknown>) ?? {})

    } else if (action === 'untrackPresence') {
      const ch = registry.get(channelName)
      if (!ch) throw new Error(`[supabaseRealtime] Channel "${channelName}" not found. Subscribe first.`)
      await ch.untrack()

    } else if (action === 'unsubscribe') {
      if (channelName === '*') {
        await registry.unsubscribeAll()
      } else {
        await registry.remove(channelName)
      }

    } else {
      throw new Error(`[supabaseRealtime] Unknown action: "${action}". Use: subscribe, unsubscribe, broadcast, trackPresence, untrackPresence`)
    }
  })
}

function supabaseContract(
  action: string,
  paths: readonly string[],
  targetParam?: string,
  defaultTarget?: string,
  defaultTargetFromParam?: { prefix: string; param: string; fallback?: string },
  requiredPaths: readonly string[] = [],
): ActionConsumerContract {
  const selector = new Set(['action', 'method'])
  const required = new Set(requiredPaths)
  return {
    schemaVersion: 'cogo.action-consumer.v1',
    action,
    runtimeAliases: [],
    reads: paths.map((path) => ({
      path,
      required: required.has(path),
      role: path === targetParam
        ? 'target'
        : selector.has(path)
          ? 'selector'
          : ['args', 'data', 'file', 'filters', 'metadata', 'order', 'payload', 'state'].includes(path)
            ? 'payload'
            : 'input',
    })),
    outcome: targetParam
      ? {
          kind: 'state-target',
          targetParam,
          ...(defaultTarget ? { defaultTarget } : {}),
          ...(defaultTargetFromParam ? { defaultTargetFromParam } : {}),
        }
      : { kind: 'none' },
    requiredHostCapabilities: ['host.supabase'],
    missingConsumerPolicy: 'block-host',
  }
}
